import { keepPreviousData, QueryClient } from "@tanstack/vue-query";
import { QUERY_RETRY_COUNT, QUERY_STALE_TIME_MS } from "@/config";
import { api, WorkersEndpoint } from "@/lib/api";
import { z } from "zod";

/**
 * TanStack Query client instance with app defaults (stale time, retry, placeholder data).
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,
      retry: QUERY_RETRY_COUNT,
      placeholderData: keepPreviousData,
    },
  },
});

type SchedulesListBatchesParameters = NonNullable<Parameters<typeof api.schedules.listBatches>[0]>;
type MaybeGetter<T> = T | (() => T | null | undefined);
type QueryKeyValue = string | number | null | undefined | (() => string | number | null | undefined);
type BatchesListQueryKeyOptions = Omit<
  SchedulesListBatchesParameters,
  | "created_at_from"
  | "created_at_to"
  | "last_run_from"
  | "last_run_to"
  | "last_updated_from"
  | "last_updated_to"
  | "scheduled_from"
  | "scheduled_to"
> & {
  created_at_from?: string | null;
  created_at_to?: string | null;
  last_run_from?: string | null;
  last_run_to?: string | null;
  last_updated_from?: string | null;
  last_updated_to?: string | null;
  scheduled_from?: string | null;
  scheduled_to?: string | null;
};

/**
 * Normalizes entity ids inside query keys so `4827` (API payload) and `"4827"` (route param)
 * hit the same cache entry. Preserves getters (lazily normalized) and non-id values.
 */
const idKey = <T>(id: T): T => {
  const normalize = (value: unknown) => (typeof value === "number" ? String(value) : value);
  return (typeof id === "function" ? () => normalize((id as () => unknown)()) : normalize(id)) as T;
};

/**
 * TanStack Query key factory for cache invalidation and prefetching.
 * Use with useQuery, useMutation, queryClient.invalidateQueries, etc.
 */
export const queryKeys = {
  projects: () => ["projects"] as const,
  project: (id: string) => ["project", id] as const,
  suites: () => ["suites"] as const,
  suite: (id: MaybeGetter<string | number>) => ["suite", idKey(id)] as const,
  suitePreconditions: (id: MaybeGetter<string | number>) => ["suite", idKey(id), "preconditions"] as const,
  playgroundSuite: (projectId: MaybeGetter<string | number>) => ["suite", "playground", idKey(projectId)] as const,
  cases: () => ["cases"] as const,
  case: (id: MaybeGetter<string | number | { id: string }>) => ["case", idKey(id)] as const,
  caseDetail: (id: MaybeGetter<string | number>) => ["case", "detail", idKey(id)] as const,
  /**
   * The version list of a case, keyed by the lineage's root rather than by the
   * case being viewed — every version of a case returns the same list, so one
   * cache entry serves them all and hopping between versions costs no request.
   * That matters because the call behind it (`cases/detail/{id}/`) can take tens
   * of seconds, and the view is force-remounted on every version hop.
   */
  caseVersions: (rootId: MaybeGetter<string | number>) => ["case", "versions", idKey(rootId)] as const,
  caseTransformed: (id: QueryKeyValue) => ["case", "transformed", idKey(id)] as const,
  cycles: () => ["cycles"] as const,
  /** Prefix of every `cycle(id)` key and what hangs under it. */
  cycleAll: () => ["cycle"] as const,
  cycle: (id: MaybeGetter<string | number>) => ["cycle", idKey(id)] as const,
  /** Under `cycle(id)`, so invalidating the cycle refreshes its review with it. */
  cycleReview: (id: MaybeGetter<string | number>) => ["cycle", idKey(id), "review"] as const,
  cycleApiRunLog: (id: MaybeGetter<string | number>) => ["cycle", idKey(id), "api-run-log"] as const,
  /** The newest passed run of a case version before a given run — "Last green run" on an API run. */
  cycleLastGreen: (caseId: QueryKeyValue, before: string) => ["cycle-last-green", caseId, before] as const,
  cycleMessages: (cycleId: QueryKeyValue, levels: readonly string[] = []) =>
    ["cycle", idKey(cycleId), "messages", [...levels].sort()] as const,
  /** Prefix of every case's run history — what starting a run invalidates. */
  cyclesLists: () => ["cycles", "list"] as const,
  /** Every run of a case version, page by page — under `cyclesLists`, so starting a run refreshes it too. */
  caseRunHistory: (caseId: QueryKeyValue) => ["cycles", "list", idKey(caseId), "all"] as const,
  schedule: (id: MaybeGetter<string | number>) => ["schedule", idKey(id)] as const,
  scheduleStreamData: (id: MaybeGetter<string | number | null | undefined>) =>
    ["schedule", idKey(id), "stream-data"] as const,
  batch: (id: string) => ["batch", id] as const,
  /** The HISTORY column: past runs of the cases in a batch, keyed by the batch so a
   *  poll of the batch itself does not refetch ~600 KB of history with it. */
  batchHistory: (id: string) => ["batch", id, "history"] as const,
  /** Under `batch(id)`, so a change that refreshes the batch refreshes its override count too. */
  batchReview: (id: string) => ["batch", id, "review"] as const,
  batchesList: (opts: BatchesListQueryKeyOptions) => ["batches", "list", opts] as const,
  /** Prefix of every `batchesList` key — invalidate this to refresh the list
   *  whatever filters it currently carries. The list is global now, so there is
   *  no per-project key left to aim at. */
  batchesLists: () => ["batches", "list"] as const,
  /** Schedules ask the same endpoint a different question (`is_pending`), so they
   *  get their own namespace — sharing `batchesList` made two views with different
   *  filters fight over one cache entry. */
  schedulesList: (opts: BatchesListQueryKeyOptions) => ["schedules", "list", opts] as const,
  schedulesLists: () => ["schedules", "list"] as const,
  /** Playground history spans every project — the project picker only scopes a new run. */
  playgroundRuns: () => ["schedules", "playground"] as const,
  batchesFilterOptions: () => ["batches", "filter-options"] as const,
  /** Dashboard aggregates. `filters` is the serialized filter object, so a
   *  change of date range or project set is a different cache entry. */
  analytics: (
    section: "summary" | "timeseries" | "timeseries-platform" | "top-failing" | "flaky" | "recent" | "scheduled",
    filters: unknown,
  ) => ["analytics", section, filters] as const,
  /** Prefix of every `analytics` key, whatever section and filters it carries. */
  analyticsAll: () => ["analytics"] as const,
  platforms: () => ["platforms"] as const,
  worker: (id: MaybeGetter<string | number>) => ["worker", idKey(id)] as const,
  workerDetails: () => ["worker"] as const,
  workers: () => ["workers"] as const,
  workersView: (parameters: z.infer<typeof WorkersEndpoint.schemas.list.parameters>) =>
    ["workers", "view", parameters] as const,
  devices: (type?: string | null, isActive?: boolean | null) => ["workers", "lookup", type, isActive] as const,
  boxZappingResults: () => ["boxZappingResults"] as const,
  artifact: (id: MaybeGetter<string | number>) => ["artifact", idKey(id)] as const,
  artifacts: (projectId: MaybeGetter<string | number>) => ["artifacts", idKey(projectId)] as const,
  /** Its own namespace, not under `project(id)`: renaming a project must not refetch its files. */
  projectKnowledge: (projectId: MaybeGetter<string | number>) => ["projectKnowledge", idKey(projectId)] as const,
  knowledgeFileContent: (fileId: MaybeGetter<string | number>) => ["knowledge", idKey(fileId), "content"] as const,
  environments: (projectId: MaybeGetter<string | number>) => ["environments", idKey(projectId)] as const,
  environment: (id: MaybeGetter<string | number>) => ["environment", idKey(id)] as const,
  clients: () => ["clients"] as const,
  clientUsers: (opts?: { search?: string; limit?: number; offset?: number; ordering?: string }) =>
    ["clientUsers", opts ?? {}] as const,
  invitations: () => ["invitations"] as const,
  invitationsList: (opts: {
    client_id?: number | null;
    search?: string | null;
    limit?: number | null;
    offset?: number | null;
  }) => ["invitations", "list", opts] as const,
  clientCurrentUsage: () => ["clientCurrentUsage"] as const,
  clientHistoricalUsage: (year: MaybeGetter<number>, month: MaybeGetter<number>) =>
    ["clientHistoricalUsage", year, month] as const,
  clientUsageHistory: (periods: Array<{ year: number; month: number }>) => ["clientUsageHistory", periods] as const,
  userProfile: () => ["userProfile"] as const,
  /** Demo-only mocks (`api.demo`, N7AIT-1419); one namespace so they are easy to find and drop. */
  demo: (section: string, ...args: unknown[]) => ["demo", section, ...args] as const,
  mcpConnections: () => ["mcp", "connections"] as const,
  mcpAuthorization: (requestId: MaybeGetter<string>) => ["mcp", "authorization", requestId] as const,
  jiraTeamConfig: (clientId: number | null) => ["jira", "team-config", clientId] as const,
  slackTeamConfig: (clientId: number | null) => ["slack", "team-config", clientId] as const,
  jiraIssueDraft: (
    cycleId: MaybeGetter<string | number>,
    stepId?: MaybeGetter<string | number | undefined>,
    projectKey?: string,
    issueType?: string,
  ) => {
    const key = ["jira", "issue-draft", idKey(cycleId), stepId === undefined ? null : idKey(stepId)] as const;
    if (issueType !== undefined) return [...key, projectKey, issueType] as const;
    return projectKey === undefined ? key : [...key, projectKey] as const;
  },
  jiraIssue: (issueId: MaybeGetter<string | number | undefined>) => ["jira", "issue", idKey(issueId)] as const,
  jiraEffectiveConfig: (projectId: MaybeGetter<string | number>) => ["jira", "effective-config", idKey(projectId)] as const,
  jiraProject: (projectId: MaybeGetter<string | number>, key: string) =>
    ["jira", "project", idKey(projectId), key] as const,
  jiraProjects: (projectId: MaybeGetter<string | number>, query = "") =>
    ["jira", "projects", idKey(projectId), query] as const,
};
